import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import MailIcon from "@mui/icons-material/Mail";

function Contact() {
  const { lang } = useParams();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({
    name: false,
    email: false,
    message: false,
    reason: false,
  });
  const [dialog, setDialog] = useState({ open: false, type: "", message: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const newErrors = {
      name: !name.trim(),
      email: !email.trim() || !emailRegex.test(email),
      message: !message.trim(),
      reason: !reason.trim(),
    };
    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      return;
    }

    try {
      const response = await fetch("/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, reason, message, lang }),
      });

      if (response.ok) {
        setDialog({
          open: true,
          type: "success",
          // Texto dinámico según idioma
          message: t.dialog.typeSuccess,
        });
        setName("");
        setEmail("");
        setReason("");
        setMessage("");
      } else {
        setDialog({
          open: true,
          type: "error",
          // Texto dinámico según idioma
          message: t.dialog.typeError,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setDialog({
        open: true,
        type: "error",
        message: t.dialog.typeError,
      });
    }
  };

  const handleCloseDialog = () => {
    setDialog({ ...dialog, open: false });
  };

  const handleChange = (event) => {
    setReason(event.target.value);
  };

  const texts = {
    es: {
      title: "Contáctanos",
      nameLabel: "Nombre",
      emailLabel: "Correo electrónico",
      messageLabel: "Mensaje",
      submit: "Enviar",
      placeholderReason: "Seleccione motivo",
      reasons: [
        { value: "info", label: "Consulta general" },
        { value: "support", label: "Soporte Pinta" },
        {
          value: "suggestions",
          label: "Sugerencias y solicitudes de funciones",
        },
        { value: "other", label: "Otros" },
      ],
      introTitle: "Estamos aquí para ti",
      introDescription: `
        Si tienes alguna pregunta o problema relacionado con Pintative o Pinta Pro, estaremos encantados de ayudarte.\n
        No dudes en enviarnos comentarios, sugerencias o solicitudes de nuevas funciones.\n
        Por favor, utiliza el formulario para ponerte en contacto con nosotros, y te responderemos lo antes posible.
      `,
      validationMessages: {
        required: "Este campo es obligatorio",
        email: "Introduce un correo electrónico válido",
      },
      errorMessages: {
        message:
          "Si el problema persiste, puede contactarnos directamente a través del correo ",
        linkText: "support@pintative.com",
      },
      dialog: {
        typeSuccess: "¡Tu mensaje ha sido enviado con éxito!",
        typeError:
          "No se pudo enviar tu mensaje. Por favor, inténtalo de nuevo más tarde.",
        successExtra: "Pronto nos pondremos en contacto contigo.",
        close: "Cerrar",
      },
    },
    en: {
      title: "Contact Us",
      nameLabel: "Name",
      emailLabel: "Email",
      messageLabel: "Message",
      submit: "Send",
      placeholderReason: "Choose a reason",
      reasons: [
        { value: "info", label: "General enquiry" },
        { value: "support", label: "Pinta support" },
        { value: "suggestions", label: "Suggestions and feature requests" },
        { value: "other", label: "Other" },
      ],
      introTitle: "We're here for you",
      introDescription: `
        If you have a question or issue regarding Pintative or Pinta Pro, we will be more than happy to assist you.\n
        Feel free to send us feedback, suggestions, or requests for new features.\n
        Please use this form to contact us, and we will get back to you as soon as possible.\n
      `,
      validationMessages: {
        required: "This field is required",
        email: "Enter a valid email",
      },
      errorMessages: {
        message: "If the problem persists, you can contact us directly at ",
        linkText: "support@pintative.com",
      },
      dialog: {
        typeSuccess: "Your message has been sent successfully!",
        typeError: "Failed to send your message. Please try again later.",
        successExtra: "We will get back to you soon.",
        close: "Close",
      },
    },
    fr: {
      title: "Contactez-nous",
      nameLabel: "Nom",
      emailLabel: "E-mail",
      messageLabel: "Message",
      submit: "Envoyer",
      placeholderReason: "Choisissez un motif",
      reasons: [
        { value: "info", label: "Demande générale" },
        { value: "support", label: "Support Pinta" },
        {
          value: "suggestions",
          label: "Suggestions et demandes de fonctionnalités",
        },
        { value: "other", label: "Autres" },
      ],
      introTitle: "Nous sommes là pour vous",
      introDescription: `
        Si vous avez une question ou un problème concernant Pintative ou Pinta Pro, nous serons ravis de vous aider.\n
        N'hésitez pas à nous faire part de vos commentaires, suggestions ou demandes de nouvelles fonctionnalités.\n
        Veuillez utiliser ce formulaire pour nous contacter, et nous vous répondrons dans les plus brefs délais.\n
      `,
      validationMessages: {
        required: "Ce champ est obligatoire",
        email: "Veuillez saisir un e-mail valide",
      },
      errorMessages: {
        message:
          "Si le problème persiste, vous pouvez nous contacter directement à l’adresse ",
        linkText: "support@pintative.com",
      },
      dialog: {
        typeSuccess: "Votre message a été envoyé avec succès !",
        typeError:
          "Impossible d’envoyer votre message. Veuillez réessayer plus tard.",
        successExtra: "Nous vous répondrons dans les plus brefs délais.",
        close: "Fermer",
      },
    },
    de: {
      title: "Kontaktieren Sie uns",
      nameLabel: "Name",
      emailLabel: "E-Mail",
      messageLabel: "Nachricht",
      submit: "Senden",
      placeholderReason: "Bitte wählen Sie einen Grund",
      reasons: [
        { value: "info", label: "Allgemeine Anfrage" },
        { value: "support", label: "Pinta-Support" },
        { value: "suggestions", label: "Vorschläge und Funktionsanfragen" },
        { value: "other", label: "Sonstiges" },
      ],
      introTitle: "Wir sind für Sie da",
      introDescription: `
        Wenn Sie eine Frage oder ein Problem in Bezug auf Pintative oder Pinta Pro haben, helfen wir Ihnen gerne weiter.\n
        Zögern Sie nicht, uns Feedback, Vorschläge oder Funktionsanfragen zukommen zu lassen.\n
        Bitte verwenden Sie dieses Formular, um uns zu kontaktieren, und wir werden Ihnen so schnell wie möglich antworten.\n
      `,
      validationMessages: {
        required: "Dieses Feld ist erforderlich",
        email: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
      },
      errorMessages: {
        message:
          "Wenn das Problem weiterhin besteht, können Sie uns direkt unter folgender Adresse kontaktieren ",
        linkText: "support@pintative.com",
      },
      dialog: {
        typeSuccess: "Ihre Nachricht wurde erfolgreich gesendet!",
        typeError:
          "Ihre Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es später erneut.",
        successExtra: "Wir werden uns so schnell wie möglich bei Ihnen melden.",
        close: "Schließen",
      },
    },
    it: {
      title: "Contattaci",
      nameLabel: "Nome",
      emailLabel: "Email",
      messageLabel: "Messaggio",
      submit: "Invia",
      placeholderReason: "Seleziona un motivo",
      reasons: [
        { value: "info", label: "Richiesta generale" },
        { value: "support", label: "Supporto Pinta" },
        {
          value: "suggestions",
          label: "Suggerimenti e richieste di funzionalità",
        },
        { value: "other", label: "Altro" },
      ],
      introTitle: "Siamo qui per te",
      introDescription: `
        Se hai una domanda o un problema riguardante Pintative o Pinta Pro, saremo felici di aiutarti.\n
        Sentiti libero di inviarci feedback, suggerimenti o richieste di nuove funzionalità.\n
        Per favore, usa questo modulo per contattarci e ti risponderemo al più presto.\n
      `,
      validationMessages: {
        required: "Questo campo è obbligatorio",
        email: "Inserisci un indirizzo email valido",
      },
      errorMessages: {
        message:
          "Se il problema persiste, puoi contattarci direttamente all’indirizzo ",
        linkText: "support@pintative.com",
      },
      dialog: {
        typeSuccess: "Il tuo messaggio è stato inviato con successo!",
        typeError:
          "Impossibile inviare il messaggio. Per favore, riprova più tardi.",
        successExtra: "Ti risponderemo al più presto.",
        close: "Chiudi",
      },
    },
  };

  // Obtenemos textos según lang, o en su defecto en inglés
  const t = texts[lang] || texts.en;

  return (
    <Box sx={{ maxWidth: 1100, margin: "0 auto", padding: 2, mt: 2 }}>
      {/* Ícono de fondo */}
      <Box
        sx={{
          position: "fixed",
          top: -90,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: -1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <MailIcon
          sx={{
            fontSize: {
              xs: "350px",
              sm: "450px",
              md: "750px",
            },
            color: "rgba(0, 0, 0, 0.015)",
          }}
        />
      </Box>

      <Grid container spacing={4}>
        {/* Columna izquierda con el texto */}
        <Grid item xs={12} md={6}>
          <Typography variant="h4" gutterBottom>
            {t.introTitle}
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
            {t.introDescription}
          </Typography>
        </Grid>

        {/* Columna derecha con el formulario */}
        <Grid item xs={12} md={6}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t.nameLabel}
              margin="normal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={errors.name}
              helperText={errors.name && t.validationMessages.required}
            />
            <TextField
              fullWidth
              label={t.emailLabel}
              margin="normal"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
              helperText={errors.email && t.validationMessages.email}
            />
            <FormControl
              variant="outlined"
              fullWidth
              error={errors.reason}
              sx={{ mt: 2 }}
            >
              <InputLabel id="reason-label">{t.placeholderReason}</InputLabel>
              <Select
                labelId="reason-label"
                value={reason}
                onChange={handleChange}
                label={t.placeholderReason}
              >
                {t.reasons.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label={t.messageLabel}
              margin="normal"
              multiline
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              error={errors.message}
              helperText={errors.message && t.validationMessages.required}
            />
            <Button variant="contained" type="submit" sx={{ mt: 2 }}>
              {t.submit}
            </Button>
          </form>
        </Grid>
      </Grid>

      {/* Dialog para mensaje de confirmación */}
      <Dialog
        open={dialog.open}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          {dialog.type === "success" ? (
            <CheckCircleOutline sx={{ color: "green", fontSize: 50 }} />
          ) : (
            <ErrorOutline sx={{ color: "red", fontSize: 50 }} />
          )}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="h6"
            align="center"
            sx={{
              fontWeight: "bold",
              color: dialog.type === "success" ? "green" : "red",
            }}
          >
            {dialog.message /* Ej: '¡Tu mensaje ha sido enviado con éxito!' */}
          </Typography>

          {dialog.type === "success" && (
            <Typography
              variant="body2"
              align="center"
              sx={{ mt: 2, color: "black" }}
            >
              {t.dialog.successExtra}
            </Typography>
          )}

          {dialog.type === "error" && (
            <Typography variant="body1" sx={{ mt: 2 }}>
              {t.errorMessages.message}
              <a
                href={`mailto:${t.errorMessages.linkText}`}
                style={{
                  textDecoration: "none",
                  fontWeight: 700,
                  color: "black",
                  transition: "all 0.3s ease",
                }}
                onMouseOver={(e) =>
                  (e.target.style.textDecoration = "underline")
                }
                onMouseOut={(e) => (e.target.style.textDecoration = "none")}
              >
                {t.errorMessages.linkText}
              </a>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} autoFocus>
            {t.dialog.close}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Contact;
